const isBlankValue = (value) =>
  value === undefined || value === null || value === "";

const normalizeOwnerFieldMetadata = (...values) =>
  values
    .filter(Boolean)
    .map((value) => String(value).replace(/([a-z0-9])([A-Z])/g, "$1 $2"))
    .join(" ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const getOwnerDetailFieldKind = (field = {}) => {
  const fieldName = String(field?.name || field?.field_name || "");
  const normalizedName = normalizeOwnerFieldMetadata(fieldName);
  const metadata = normalizeOwnerFieldMetadata(
    fieldName,
    field?.label,
    field?.field_label,
    field?.title,
  );

  const hasOwnerContext =
    /\b(?:property\s+|home\s+)?owner(?:ship)?\b/.test(metadata) ||
    /^o\s+(?:address|street|city|locality|place|state|province|zip|postal|phone|mobile|cell|email)\b/.test(
      normalizedName,
    );

  if (!hasOwnerContext) return null;

  const isSecondaryAddressField =
    /\b(?:address|street)(?:\s+line)?\s*(?:2|unit|suite|apt|apartment)\b/.test(
      metadata,
    );
  if (isSecondaryAddressField) return null;

  const detailPatterns = [
    ["email", /\b(?:email|e\s+mail)\b/],
    ["phone", /\b(?:phone|mobile|cell|telephone)\b/],
    ["city", /\b(?:city|locality|place(?:\s+name)?)\b/],
    ["state", /\b(?:state|province)\b/],
    ["zip", /\b(?:zip(?:\s+code)?|postal(?:\s+code)?|postcode)\b/],
  ];
  const detailKind = detailPatterns.find(([, pattern]) =>
    pattern.test(metadata),
  )?.[0];

  if (detailKind) return detailKind;
  if (/\bstreet\b/.test(normalizedName)) return "street";
  if (/\b(?:address|street)(?:\s+line)?\b/.test(metadata)) return "address";

  return null;
};

export const applyOwnerDetailPrefills = ({
  updatedValues,
  fields = [],
  defaultFormFields = [],
  selectedRow,
}) => {
  if (!updatedValues || !Array.isArray(fields)) return updatedValues;

  const propertyOwner = selectedRow?.property_owner || {};
  const addressParts = propertyOwner?.address_parts || {};
  const detailValues = {
    street:
      addressParts?.address1 || addressParts?.address || propertyOwner?.address,
    address:
      propertyOwner?.address || addressParts?.address || addressParts?.address1,
    city:
      addressParts?.city ||
      propertyOwner?.city ||
      selectedRow?.chunked_addresses?.owner_address?.place_name,
    state: addressParts?.state || propertyOwner?.state,
    zip:
      addressParts?.zip_code ||
      addressParts?.zipCode ||
      propertyOwner?.zip ||
      propertyOwner?.postalCode,
    phone:
      propertyOwner?.mobile ||
      propertyOwner?.phone ||
      propertyOwner?.phone_name,
    email: propertyOwner?.email,
  };
  const ownerDetailKindByFieldId = new Map();
  const fieldsByName = new Map();

  (Array.isArray(defaultFormFields) ? defaultFormFields : []).forEach(
    (field) => {
      const detailKind = getOwnerDetailFieldKind(field);

      if (field?.field_id && detailKind) {
        ownerDetailKindByFieldId.set(field.field_id, detailKind);
      }
    },
  );

  fields.forEach((field) => {
    if (field?.name) fieldsByName.set(field.name, field);
  });
  Object.keys(updatedValues).forEach((fieldName) => {
    if (!fieldsByName.has(fieldName)) {
      fieldsByName.set(fieldName, { name: fieldName });
    }
  });

  fieldsByName.forEach((field, fieldName) => {
    const detailKind =
      ownerDetailKindByFieldId.get(field?.field_id) ||
      getOwnerDetailFieldKind(field);
    const detailValue = detailValues[detailKind];

    if (
      detailKind &&
      isBlankValue(updatedValues[fieldName]) &&
      !isBlankValue(detailValue)
    ) {
      updatedValues[fieldName] = detailValue;
    }
  });

  return updatedValues;
};
